import { useRef, useState, useCallback } from 'react';
import { UploadCloud, FileText, FileImage, X, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

const ACCEPTED_MIME_TYPES: Record<string, string> = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'image/tiff': 'TIFF',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
};
const MAX_BYTES = 50 * 1024 * 1024; // 50 MB

interface FileUploadZoneProps {
  file: File | null;
  onFileChange: (file: File | null) => void;
  uploadProgress: number | null;
  isUploading: boolean;
  className?: string;
}

function fileIcon(mimeType: string): React.ReactNode {
  if (mimeType.startsWith('image/')) {
    return <FileImage className="h-8 w-8 text-muted-foreground" aria-hidden="true" />;
  }
  return <FileText className="h-8 w-8 text-muted-foreground" aria-hidden="true" />;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FileUploadZone({
  file,
  onFileChange,
  uploadProgress,
  isUploading,
  className,
}: FileUploadZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validate = useCallback((candidate: File): string | null => {
    if (!ACCEPTED_MIME_TYPES[candidate.type]) {
      return 'Unsupported file format. Please upload a PDF, DOCX, TIFF, JPEG, or PNG file.';
    }
    if (candidate.size > MAX_BYTES) {
      return `File is too large (${formatBytes(candidate.size)}). Maximum size is 50 MB.`;
    }
    return null;
  }, []);

  const handleFile = useCallback((candidate: File) => {
    const error = validate(candidate);
    if (error) {
      setValidationError(error);
      onFileChange(null);
    } else {
      setValidationError(null);
      onFileChange(candidate);
    }
  }, [validate, onFileChange]);

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files.item(0);
    if (dropped) handleFile(dropped);
  }, [handleFile]);

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => { setIsDragging(false); };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) handleFile(selected);
    e.target.value = '';
  };

  const removeFile = () => {
    setValidationError(null);
    onFileChange(null);
  };

  const openPicker = () => inputRef.current?.click();

  const errorId = 'file-upload-error';

  return (
    <div className={cn('space-y-2', className)}>
      <input
        ref={inputRef}
        id="contract-file-input"
        type="file"
        accept={Object.keys(ACCEPTED_MIME_TYPES).join(',')}
        className="sr-only"
        aria-label="Select contract file"
        onChange={onInputChange}
        disabled={isUploading}
      />

      {file ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 p-4">
          {fileIcon(file.type)}
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
            <p className="text-xs text-muted-foreground">
              {ACCEPTED_MIME_TYPES[file.type]} &middot; {formatBytes(file.size)}
            </p>
          </div>
          {!isUploading && (
            <button
              type="button"
              onClick={removeFile}
              className="rounded p-1 text-muted-foreground hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              aria-label="Remove selected file"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-label="Drop zone: drag and drop a contract file here, or press Enter to browse"
          aria-describedby={validationError ? errorId : undefined}
          aria-invalid={!!validationError}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onClick={openPicker}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openPicker(); }}
          className={cn(
            'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-10 text-center transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/60 hover:bg-muted/30',
            validationError && 'border-destructive',
          )}
        >
          <UploadCloud
            className={cn('h-10 w-10', isDragging ? 'text-primary' : 'text-muted-foreground')}
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-medium text-foreground">
              Drag and drop your contract here, or click to browse
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Accepted formats: PDF, DOCX, TIFF, JPEG, PNG. Max size: 50 MB.
            </p>
          </div>
        </div>
      )}

      {validationError && (
        <p id={errorId} role="alert" className="flex items-center gap-1.5 text-sm text-destructive">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {validationError}
        </p>
      )}

      {uploadProgress !== null && (
        <div aria-live="polite" aria-atomic="true">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
            <span>Uploading…</span>
            <span>{uploadProgress}%</span>
          </div>
          <div
            role="progressbar"
            aria-valuenow={uploadProgress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="Upload progress"
            className={cn(
              'h-2 w-full overflow-hidden rounded-full bg-muted',
              uploadProgress === -1 && 'border border-destructive',
            )}
          >
            <div
              className={cn(
                'h-full transition-all duration-300',
                uploadProgress === -1 ? 'bg-destructive w-full' : 'bg-primary',
              )}
              style={uploadProgress >= 0 ? { width: `${uploadProgress}%` } : undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
}
